/**
 * Auth store. Access token lives in memory only — never localStorage — so
 * an XSS bug can't exfiltrate a long-lived credential. Refresh token is an
 * httpOnly cookie the JS can't touch.
 *
 * On page reload, the store starts empty; the app calls `/auth/refresh` at
 * boot to hydrate from the cookie.
 */

import { create } from 'zustand';
import type { AuthenticatedUser } from '@fuelsync/common';

interface AuthState {
  accessToken: string | null;
  accessTokenExpiresAt: string | null; // ISO
  user: AuthenticatedUser | null;
  /**
   * Durable flag: this tab is an impersonation session. Set as soon as the
   * impersonation URL params are read (before /auth/me returns), so any 401
   * during the initial hydrate can't be silently "recovered" by falling
   * back to the admin's refresh cookie (which the browser sends to the
   * same-origin backend on every request).
   *
   * Used by tryRefresh and useLogout to refuse network calls that would
   * otherwise interact with the admin's cookie in this tab.
   */
  isImpersonating: boolean;

  setSession: (opts: {
    accessToken: string;
    accessTokenExpiresAt: string;
    user: AuthenticatedUser;
  }) => void;
  setAccessToken: (accessToken: string, accessTokenExpiresAt: string) => void;
  setUser: (user: AuthenticatedUser) => void;
  markImpersonating: () => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  accessTokenExpiresAt: null,
  user: null,
  isImpersonating: false,

  setSession: ({ accessToken, accessTokenExpiresAt, user }) =>
    set({ accessToken, accessTokenExpiresAt, user, isImpersonating: !!user.impersonatedBy }),
  setAccessToken: (accessToken, accessTokenExpiresAt) => set({ accessToken, accessTokenExpiresAt }),
  setUser: (user) => set({ user, isImpersonating: !!user.impersonatedBy }),
  markImpersonating: () => set({ isImpersonating: true }),
  clear: () =>
    set({ accessToken: null, accessTokenExpiresAt: null, user: null, isImpersonating: false }),
}));
